import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import PhoneInput, {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import en from "react-phone-number-input/locale/en.json";
import * as Flags from "country-flag-icons/react/3x2";

type Props = {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
};

const dropdownPaperStyles = {
  backgroundColor: "#1d1c1b",
  color: "white",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
};

const scrollbarStyles = {
  "&::-webkit-scrollbar": { width: "10px" },
  "&::-webkit-scrollbar-track": { background: "#181716", borderRadius: "10px" },
  "&::-webkit-scrollbar-thumb": {
    background: "#4b4743",
    borderRadius: "10px",
    border: "2px solid #181716",
  },
  "&::-webkit-scrollbar-thumb:hover": { background: "#5a5550" },
  scrollbarWidth: "thin" as const,
  scrollbarColor: "#4b4743 #181716",
};

const CountryFlag: React.FC<{ code?: string; size?: number; mr?: number }> = ({
  code,
  size = 20,
  mr = 8,
}) => {
  const Flag = code ? Flags[code as keyof typeof Flags] : undefined;
  if (!Flag) return null;
  return (
    <Flag
      style={{
        width: size,
        height: Math.round(size * 0.7),
        marginRight: mr,
        borderRadius: 2,
        flexShrink: 0,
      }}
    />
  );
};

export const MuiPhoneInput = React.forwardRef<HTMLInputElement, any>(
  function MuiPhoneInput(props, ref) {
    return <TextField {...props} inputRef={ref} fullWidth variant="outlined" />;
  },
);

export const MuiCountrySelect = ({ value, onChange }: any) => {
  return (
    <Select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      displayEmpty
      sx={{ minWidth: 120 }}
    >
      {getCountries().map((country) => (
        <MenuItem key={country} value={country}>
          {(en as any)[country] || country} +{getCountryCallingCode(country)}
        </MenuItem>
      ))}
    </Select>
  );
};

const countries = getCountries().map((code) => ({
  code,
  label: (en as any)[code] || code,
  phone: getCountryCallingCode(code),
}));

/* The field keeps the dialling code after the number is erased — that is an
   empty phone number, not an invalid one. */
const withoutBareDiallingCode = (value?: string) =>
  !value || countries.some((c) => value === `+${c.phone}`) ? "" : value;

export const CountrySelect = ({ value, onChange }: any) => {
  return (
    <Autocomplete
      options={countries}
      value={countries.find((c) => c.code === value) || null}
      onChange={(_, newValue) => onChange(newValue?.code)}
      getOptionLabel={(option) => `${option.label} (+${option.phone})`}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            backgroundColor: "#1d1c1b",
            color: "white",
            "&.Mui-focused": { backgroundColor: "#2a2928" },
            '&[aria-selected="true"]': { backgroundColor: "#2f2d2b" },
            '&[aria-selected="true"].Mui-focused': {
              backgroundColor: "#3a3836",
            },
          }}
        >
          <CountryFlag code={option.code} size={20} mr={8} />
          {option.label} (+{option.phone})
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label="Country" />}
      sx={{ width: 250 }}
      slotProps={{
        paper: { sx: dropdownPaperStyles },
        listbox: {
          sx: {
            padding: "6px",
            backgroundColor: "#1d1c1b",
            "& .MuiAutocomplete-option": {
              borderRadius: "8px",
              minHeight: "40px",
              alignItems: "center",
            },
            "& .MuiAutocomplete-option.Mui-focused": {
              backgroundColor: "#2a2928",
            },
            "& .MuiAutocomplete-option[aria-selected='true']": {
              backgroundColor: "#2f2d2b",
            },
            "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
              backgroundColor: "#3a3836",
            },
            ...scrollbarStyles,
          },
        },
      }}
    />
  );
};

export const CompactCountrySelect = ({ value, onChange }: any) => {
  return (
    <Select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      MenuProps={{
        PaperProps: {
          sx: { maxHeight: 320, ...dropdownPaperStyles, ...scrollbarStyles },
        },
        MenuListProps: { sx: { padding: "6px" } },
      }}
      renderValue={(selected: any) => (
        <Box
          sx={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}
        >
          <CountryFlag code={selected} size={22} mr={8} />
          {selected ? `+${getCountryCallingCode(selected)}` : ""}
        </Box>
      )}
      sx={{
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      {countries.map((c) => (
        <MenuItem
          key={c.code}
          value={c.code}
          sx={{
            color: "white",
            borderRadius: "8px",
            minHeight: "40px",
            "&:hover": { backgroundColor: "#2a2928" },
            "&.Mui-selected": { backgroundColor: "#2f2d2b" },
            "&.Mui-selected:hover": { backgroundColor: "#3a3836" },
          }}
        >
          <CountryFlag code={c.code} size={20} mr={8} />
          {c.label} (+{c.phone})
        </MenuItem>
      ))}
    </Select>
  );
};

export const PhoneNumberInput: React.FC<Props> = ({
  value,
  onChange,
  error,
  helperText,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <Box
      sx={{
        "& .PhoneInput": {
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: "10px", lg: "26px" },
        },
        "& .PhoneInputCountry": { margin: 0, flexShrink: 0 },
      }}
    >
      <PhoneInput
        international
        defaultCountry="DE"
        value={value}
        onChange={(val) => onChange(withoutBareDiallingCode(val))}
        inputComponent={MuiPhoneInput}
        countrySelectComponent={isMobile ? CompactCountrySelect : CountrySelect}
        error={Boolean(error)}
        helperText={helperText}
      />
    </Box>
  );
};
